import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BackupIcon from '@mui/icons-material/Backup';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SecurityIcon from '@mui/icons-material/Security';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import ComputerIcon from '@mui/icons-material/Computer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmDialog from './ConfirmDialog';

interface VM {
  vmid: number;
  name: string;
  status: string;
  node: string;
  cpu: number;
  cpus: number;
  mem: number;
  maxmem: number;
  disk: number;
  maxdisk: number;
  uptime: number;
  type: string;
}

interface VMDetailsProps {
  vm: VM;
  token: string;
  onBack: () => void;
}

const VMDetails: React.FC<VMDetailsProps> = ({ vm, token, onBack }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  
  // Notification system
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  const [operationInProgress, setOperationInProgress] = useState<string | null>(null);
  
  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
    severity: 'warning' | 'error' | 'info';
  }>({ open: false, title: '', message: '', action: () => {}, severity: 'warning' });
  
  // Snapshots
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [snapshotDialog, setSnapshotDialog] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotDesc, setSnapshotDesc] = useState('');
  
  // Firewall
  const [firewallEnabled, setFirewallEnabled] = useState(false);
  
  // Backup Schedule
  const [backupScheduleDialog, setBackupScheduleDialog] = useState(false);
  const [editingJob, setEditingJob] = useState<any>(null);
  const [scheduleData, setScheduleData] = useState({ dow: '', starttime: '' });
  const [scheduledJobs, setScheduledJobs] = useState<any[]>([]);
  
  // Backups list
  const [backups, setBackups] = useState<any[]>([]);
  
  // Activity logs
  const [activityLogs, setActivityLogs] = useState<any[]>([]);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchSnapshots();
    } else if (activeTab === 2) {
      fetchBackups();
      fetchScheduledJobs();
    } else if (activeTab === 3) {
      fetchFirewallStatus();
    } else if (activeTab === 4) {
      fetchActivityLogs();
    }
  }, [activeTab]);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/snapshots?vm_type=${vm.type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch snapshots');
      const data = await res.json();
      setSnapshots(data);
    } catch (err: any) {
      setError(err.message || 'Error fetching snapshots');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/backups`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch backups');
      const data = await res.json();
      setBackups(data);
    } catch (err: any) {
      setError(err.message || 'Error fetching backups');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledJobs = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/backup-jobs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch backup jobs');
      const data = await res.json();
      // Filter jobs for this VM
      const vmJobs = data.filter((job: any) => job.vmid && job.vmid.includes(String(vm.vmid)));
      setScheduledJobs(vmJobs);
    } catch (err: any) {
      console.error('Error fetching scheduled jobs:', err);
    }
  };

  const handleDeleteBackupJob = (jobId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Backup Schedule',
      message: `Are you sure you want to delete this backup schedule? This will stop automatic backups.`,
      severity: 'warning',
      action: () => executeDeleteBackupJob(jobId)
    });
  };

  const executeDeleteBackupJob = async (jobId: string) => {
    const key = `delete-job-${jobId}`;
    if (actionLoading[key]) return;
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/backup-jobs/${jobId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete backup job');
      }
      showNotification('Backup schedule deleted successfully', 'success');
      await fetchScheduledJobs();
      setActionLoading({ ...actionLoading, [key]: false });
    } catch (err: any) {
      setError(err.message || 'Error deleting backup job');
      showNotification(err.message || 'Error deleting backup job', 'error');
      setActionLoading({ ...actionLoading, [key]: false });
    }
  };

  const handleEditBackupJob = (job: any) => {
    setEditingJob(job);
    setScheduleData({
      dow: job.dow || '',
      starttime: job.starttime || ''
    });
    setBackupScheduleDialog(true);
  };

  const handleDeleteBackup = (volid: string, storage: string) => {
    const fileName = volid.split('/').pop();
    setConfirmDialog({
      open: true,
      title: 'Delete Backup File',
      message: `Are you sure you want to permanently delete backup "${fileName}"? This action cannot be undone.`,
      severity: 'error',
      action: () => executeDeleteBackup(volid, storage)
    });
  };

  const executeDeleteBackup = async (volid: string, storage: string) => {
    const key = `delete-backup-${volid}`;
    if (actionLoading[key]) return;
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/backups/${vm.node}/${storage}/${encodeURIComponent(volid)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete backup');
      }
      showNotification('Backup file deleted successfully', 'success');
      await fetchBackups();
      setActionLoading({ ...actionLoading, [key]: false });
    } catch (err: any) {
      setError(err.message || 'Error deleting backup');
      showNotification(err.message || 'Error deleting backup', 'error');
      setActionLoading({ ...actionLoading, [key]: false });
    }
  };

  const handleRestoreBackup = async (archive: string) => {
    if (!window.confirm(`Are you sure you want to restore from this backup? This will overwrite the current VM state.`)) return;

    const key = `${vm.vmid}-restore`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    setOperationInProgress('Restoring backup...');
    showNotification('Restore started. This may take several minutes...', 'info');
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/restore?vm_type=${vm.type}&archive=${encodeURIComponent(archive)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to restore backup');
      }
      
      const result = await res.json();
      const taskId = result.task; // UPID from Proxmox
      
      // Poll for task completion
      if (taskId) {
        await pollTaskStatus(vm.node, taskId, key, 'Backup restored successfully!');
      } else {
        // No task ID, just refresh after delay
        setTimeout(async () => {
          await fetchBackups();
          showNotification('Backup restored successfully!', 'success');
          setOperationInProgress(null);
          setActionLoading(prev => ({ ...prev, [key]: false }));
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Error restoring backup');
      showNotification(err.message || 'Error restoring backup', 'error');
      setOperationInProgress(null);
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const fetchFirewallStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/firewall?vm_type=${vm.type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch firewall status');
      const data = await res.json();
      setFirewallEnabled(data.enable === 1);
    } catch (err: any) {
      setError(err.message || 'Error fetching firewall status');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/nodes/${vm.node}/tasks?vmid=${vm.vmid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch activity logs');
      const data = await res.json();
      setActivityLogs(data);
    } catch (err: any) {
      setError(err.message || 'Error fetching activity logs');
    } finally {
      setLoading(false);
    }
  };

  const pollTaskStatus = async (node: string, upid: string, actionKey: string, successMessage: string) => {
    const maxAttempts = 60; // Max 60 seconds
    let attempts = 0;
    
    const poll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/proxmox/tasks/${node}/${encodeURIComponent(upid)}/status`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (res.ok) {
          const status = await res.json();
          
          if (status.status === 'stopped') {
            // Task completed
            setOperationInProgress(null);
            showNotification(successMessage, 'success');
            
            // Refresh data based on action type
            if (actionKey.includes('snapshot')) {
              await fetchSnapshots();
            } else if (actionKey.includes('backup')) {
              await fetchBackups();
            }
            
            setActionLoading(prev => ({ ...prev, [actionKey]: false }));
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000); // Poll every second
        } else {
          // Timeout, refresh anyway
          setOperationInProgress(null);
          showNotification('Operation timeout, please check activity logs', 'warning');
          
          if (actionKey.includes('snapshot')) {
            await fetchSnapshots();
          } else if (actionKey.includes('backup')) {
            await fetchBackups();
          }
          
          setActionLoading(prev => ({ ...prev, [actionKey]: false }));
        }
      } catch (err) {
        // On error, just refresh and re-enable
        setOperationInProgress(null);
        
        if (actionKey.includes('snapshot')) {
          await fetchSnapshots();
        } else if (actionKey.includes('backup')) {
          await fetchBackups();
        }
        
        setActionLoading(prev => ({ ...prev, [actionKey]: false }));
      }
    };
    
    poll();
  };

  const handleVMAction = async (action: string) => {
    const key = `${vm.vmid}-${action}`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/${action}?vm_type=${vm.type}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to ${action} VM`);
      }
      // Success - reload page to show new status
      window.location.reload();
    } catch (err: any) {
      setError(err.message || `Error ${action}ing VM`);
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
    // Note: No finally block - button stays disabled on success since page reloads
  };

  const handleCreateSnapshot = async () => {
    if (!snapshotName) return;
    
    const key = `${vm.vmid}-snapshot`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    setOperationInProgress('Creating snapshot...');
    showNotification('Creating snapshot...', 'info');
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/snapshot?vm_type=${vm.type}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ snapname: snapshotName, description: snapshotDesc }),
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create snapshot');
      }
      
      const result = await res.json();
      const taskId = result.task; // UPID from Proxmox
      
      // Close dialog immediately
      setSnapshotDialog(false);
      setSnapshotName('');
      setSnapshotDesc('');
      
      // Poll for task completion
      if (taskId) {
        await pollTaskStatus(vm.node, taskId, key, 'Snapshot created successfully!');
      } else {
        // No task ID, just refresh after delay
        setTimeout(async () => {
          await fetchSnapshots();
          showNotification('Snapshot created successfully!', 'success');
          setOperationInProgress(null);
          setActionLoading(prev => ({ ...prev, [key]: false }));
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Error creating snapshot');
      showNotification(err.message || 'Error creating snapshot', 'error');
      setOperationInProgress(null);
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const handleRollbackSnapshot = async (snapname: string) => {
    if (!window.confirm(`Are you sure you want to restore to snapshot "${snapname}"?`)) return;
    
    const key = `${vm.vmid}-rollback`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    setOperationInProgress('Restoring snapshot...');
    showNotification('Restoring snapshot. This may take a few minutes...', 'info');
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/snapshot/${snapname}/rollback?vm_type=${vm.type}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to rollback snapshot');
      }
      
      const result = await res.json();
      const taskId = result.task; // UPID from Proxmox
      
      // Poll for task completion
      if (taskId) {
        await pollTaskStatus(vm.node, taskId, key, 'Snapshot restored successfully!');
      } else {
        // No task ID, just refresh
        await fetchSnapshots();
        showNotification('Snapshot restored successfully!', 'success');
        setOperationInProgress(null);
        setActionLoading(prev => ({ ...prev, [key]: false }));
      }
    } catch (err: any) {
      setError(err.message || 'Error rolling back snapshot');
      showNotification(err.message || 'Error rolling back snapshot', 'error');
      setOperationInProgress(null);
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const handleDeleteSnapshot = (snapname: string) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Snapshot',
      message: `Are you sure you want to permanently delete snapshot "${snapname}"? This action cannot be undone.`,
      severity: 'error',
      action: () => executeDeleteSnapshot(snapname)
    });
  };
  
  const executeDeleteSnapshot = async (snapname: string) => {
    const key = `${vm.vmid}-deletesnap`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/snapshot/${snapname}?vm_type=${vm.type}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete snapshot');
      }
      await fetchSnapshots();
      setActionLoading({ ...actionLoading, [key]: false });
    } catch (err: any) {
      setError(err.message || 'Error deleting snapshot');
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const handleBackupNow = async () => {
    const key = `${vm.vmid}-backup`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    setOperationInProgress('Creating backup...');
    showNotification('Backup started. This may take several minutes...', 'info');
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/backup?vm_type=${vm.type}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to start backup');
      }
      
      const result = await res.json();
      const taskId = result.task; // UPID from Proxmox
      
      // Poll for task completion
      if (taskId) {
        await pollTaskStatus(vm.node, taskId, key, 'Backup completed successfully!');
      } else {
        // No task ID, just refresh after delay
        setTimeout(async () => {
          await fetchBackups();
          showNotification('Backup completed!', 'success');
          setOperationInProgress(null);
          setActionLoading(prev => ({ ...prev, [key]: false }));
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Error starting backup');
      showNotification(err.message || 'Error starting backup', 'error');
      setOperationInProgress(null);
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const handleScheduleBackup = async () => {
    if (!scheduleData.dow || !scheduleData.starttime) return;
    
    const key = `${vm.vmid}-schedule`;
    if (actionLoading[key]) return;
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      if (editingJob) {
        // Update existing job
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/proxmox/backup-jobs/${editingJob.id}?dow=${scheduleData.dow}&starttime=${scheduleData.starttime}`,
          {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to update backup schedule');
        }
        showNotification('Backup schedule updated successfully', 'success');
      } else {
        // Create new job
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/backup-schedule?vm_type=${vm.type}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(scheduleData),
          }
        );
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to schedule backup');
        }
        showNotification('Backup scheduled successfully', 'success');
      }
      
      // Close dialog and refresh
      setBackupScheduleDialog(false);
      setScheduleData({ dow: '', starttime: '' });
      setEditingJob(null);
      await fetchScheduledJobs();
      setActionLoading({ ...actionLoading, [key]: false });
    } catch (err: any) {
      setError(err.message || 'Error saving backup schedule');
      showNotification(err.message || 'Error saving backup schedule', 'error');
      setActionLoading({ ...actionLoading, [key]: false });
    }
  };

  const handleToggleFirewall = async () => {
    const key = `${vm.vmid}-firewall`;
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${vm.node}/${vm.vmid}/firewall?vm_type=${vm.type}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ enable: !firewallEnabled }),
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update firewall');
      }
      // Success - update state and re-enable
      setFirewallEnabled(!firewallEnabled);
      alert('Firewall settings updated successfully');
      setActionLoading({ ...actionLoading, [key]: false });
    } catch (err: any) {
      setError(err.message || 'Error updating firewall');
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const isRunning = vm.status === 'running';
  const isStopped = vm.status === 'stopped';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {vm.name}
        </Typography>
        <Chip
          label={vm.status}
          size="medium"
          color={isRunning ? 'success' : 'default'}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* VM Control Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Actions</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isStopped && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={() => handleVMAction('start')}
              disabled={actionLoading[`${vm.vmid}-start`]}
            >
              Start
            </Button>
          )}
          {isRunning && (
            <>
              <Button
                variant="contained"
                color="warning"
                startIcon={<PowerSettingsNewIcon />}
                onClick={() => handleVMAction('shutdown')}
                disabled={actionLoading[`${vm.vmid}-shutdown`]}
              >
                Shutdown
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={() => handleVMAction('stop')}
                disabled={actionLoading[`${vm.vmid}-stop`]}
              >
                Force Stop
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RestartAltIcon />}
                onClick={() => handleVMAction('reboot')}
                disabled={actionLoading[`${vm.vmid}-reboot`]}
              >
                Reboot
              </Button>
              {vm.type === 'qemu' && (
                <Button
                  variant="outlined"
                  startIcon={<RotateLeftIcon />}
                  onClick={() => handleVMAction('reset')}
                  disabled={actionLoading[`${vm.vmid}-reset`]}
                >
                  Reset
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* VM Information Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>CPU Usage</Typography>
              <Typography variant="h5">
                {vm.cpus > 0 ? ((vm.cpu / vm.cpus) * 100).toFixed(1) : '0.0'}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vm.cpus} cores
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Memory</Typography>
              <Typography variant="h5">
                {vm.maxmem > 0 ? ((vm.mem / vm.maxmem) * 100).toFixed(1) : '0'}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatBytes(vm.mem)} / {formatBytes(vm.maxmem)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Disk</Typography>
              <Typography variant="h5">{formatBytes(vm.disk)}</Typography>
              <Typography variant="body2" color="text.secondary">
                / {formatBytes(vm.maxdisk)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Uptime</Typography>
              <Typography variant="h5">{formatUptime(vm.uptime)}</Typography>
              <Typography variant="body2" color="text.secondary">
                Node: {vm.node}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="Snapshots" />
          <Tab label="Backups" />
          <Tab label="Firewall" />
          <Tab label="Activity Logs" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>VM Information</Typography>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>VM ID</strong></TableCell>
                      <TableCell>{vm.vmid}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell>{vm.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell>
                        <Chip label={vm.status} size="small" color={isRunning ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Node</strong></TableCell>
                      <TableCell>{vm.node}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>CPU Cores</strong></TableCell>
                      <TableCell>{vm.cpus}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Memory</strong></TableCell>
                      <TableCell>{formatBytes(vm.maxmem)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Disk</strong></TableCell>
                      <TableCell>{formatBytes(vm.maxdisk)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Snapshots Tab */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Snapshots</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <IconButton onClick={fetchSnapshots} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={<CameraAltIcon />}
                    onClick={() => setSnapshotDialog(true)}
                  >
                    Create Snapshot
                  </Button>
                </Box>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : snapshots.length === 0 ? (
                <Typography color="text.secondary">No snapshots available</Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {snapshots.map((snap: any) => (
                        <TableRow key={snap.name}>
                          <TableCell>{snap.name}</TableCell>
                          <TableCell>{snap.description || '-'}</TableCell>
                          <TableCell>
                            {snap.snaptime ? new Date(snap.snaptime * 1000).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Restore">
                              <IconButton
                                color="primary"
                                onClick={() => handleRollbackSnapshot(snap.name)}
                                disabled={actionLoading[`${vm.vmid}-rollback`]}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteSnapshot(snap.name)}
                                disabled={actionLoading[`${vm.vmid}-deletesnap`]}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Backups Tab */}
          {activeTab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Backup Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <IconButton 
                    onClick={() => {
                      fetchBackups();
                      fetchScheduledJobs();
                    }} 
                    disabled={loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={<BackupIcon />}
                    onClick={handleBackupNow}
                    disabled={actionLoading[`${vm.vmid}-backup`]}
                  >
                    {actionLoading[`${vm.vmid}-backup`] ? 'Backing up...' : 'Backup Now'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    onClick={() => setBackupScheduleDialog(true)}
                  >
                    Schedule Backup
                  </Button>
                </Box>
              </Box>

              {/* Scheduled Backups Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Scheduled Backups
                </Typography>
                {scheduledJobs.length === 0 ? (
                  <Typography color="text.secondary">No backup schedules configured for this VM</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Schedule</TableCell>
                          <TableCell>Days</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Storage</TableCell>
                          <TableCell>Enabled</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scheduledJobs.map((job: any) => (
                          <TableRow key={job.id}>
                            <TableCell>
                              <Chip label={`Job ${job.id}`} size="small" color="primary" />
                            </TableCell>
                            <TableCell>{job.dow || 'Daily'}</TableCell>
                            <TableCell>{job.starttime || 'N/A'}</TableCell>
                            <TableCell>{job.storage || 'Default'}</TableCell>
                            <TableCell>
                              <Chip
                                label={job.enabled ? 'Yes' : 'No'}
                                size="small"
                                color={job.enabled ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Edit schedule">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditBackupJob(job)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete schedule">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteBackupJob(job.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Backup History Section */}
              <Typography variant="h6" gutterBottom>
                Backup History
              </Typography>
              
              {loading ? (
                <CircularProgress />
              ) : backups.length === 0 ? (
                <Typography color="text.secondary">No backups available for this VM</Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Backup File</TableCell>
                        <TableCell>Storage</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Format</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backups.map((backup: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{backup.volid?.split('/').pop() || backup.volid}</TableCell>
                          <TableCell>{backup.storage}</TableCell>
                          <TableCell>
                            {backup.size ? (backup.size / 1024 / 1024 / 1024).toFixed(2) + ' GB' : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {backup.ctime ? new Date(backup.ctime * 1000).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip label={backup.format || 'Unknown'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Restore from this backup">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleRestoreBackup(backup.volid)}
                                disabled={actionLoading[`${vm.vmid}-restore`]}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete backup file">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBackup(backup.volid, backup.storage)}
                                disabled={actionLoading[`delete-backup-${backup.volid}`]}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Firewall Tab */}
          {activeTab === 3 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Firewall Configuration</Typography>
                <IconButton onClick={fetchFirewallStatus} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : (
                <Box>
                  <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                    VM Firewall is currently: <strong>{firewallEnabled ? 'ENABLED' : 'DISABLED'}</strong>
                  </Typography>
                  <Button
                    variant="contained"
                    color={firewallEnabled ? 'error' : 'success'}
                    startIcon={<SecurityIcon />}
                    onClick={handleToggleFirewall}
                    disabled={actionLoading[`${vm.vmid}-firewall`]}
                  >
                    {firewallEnabled ? 'Disable' : 'Enable'} Firewall
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Enable the firewall to protect this VM with firewall rules.
                    Firewall rules can be managed in the web interface.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Activity Logs Tab */}
          {activeTab === 4 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Recent Activity</Typography>
                <IconButton onClick={fetchActivityLogs} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : activityLogs.length === 0 ? (
                <Typography color="text.secondary">No activity logs available</Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Task</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activityLogs.map((log: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{new Date(log.starttime * 1000).toLocaleString()}</TableCell>
                          <TableCell>{log.type}</TableCell>
                          <TableCell>
                            <Chip 
                              label={log.status} 
                              size="small" 
                              color={
                                log.status === 'OK' ? 'success' : 
                                log.status === 'running' ? 'info' : 
                                'default'
                              } 
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Create Snapshot Dialog */}
      <Dialog open={snapshotDialog} onClose={() => setSnapshotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Snapshot</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Snapshot Name"
            fullWidth
            value={snapshotName}
            onChange={(e) => setSnapshotName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            value={snapshotDesc}
            onChange={(e) => setSnapshotDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotDialog(false)} disabled={actionLoading[`${vm.vmid}-snapshot`]}>Cancel</Button>
          <Button 
            onClick={handleCreateSnapshot} 
            variant="contained" 
            disabled={!snapshotName || actionLoading[`${vm.vmid}-snapshot`]}
          >
            {actionLoading[`${vm.vmid}-snapshot`] ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Schedule Dialog */}
      <Dialog 
        open={backupScheduleDialog} 
        onClose={() => {
          setBackupScheduleDialog(false);
          setEditingJob(null);
          setScheduleData({ dow: '', starttime: '' });
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{editingJob ? 'Edit Backup Schedule' : 'Schedule Backup'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Day of Week"
            fullWidth
            value={scheduleData.dow}
            onChange={(e) => setScheduleData({ ...scheduleData, dow: e.target.value })}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            <option value="">Select day...</option>
            <option value="mon">Monday</option>
            <option value="tue">Tuesday</option>
            <option value="wed">Wednesday</option>
            <option value="thu">Thursday</option>
            <option value="fri">Friday</option>
            <option value="sat">Saturday</option>
            <option value="sun">Sunday</option>
            <option value="*">Every day</option>
          </TextField>
          <TextField
            margin="dense"
            label="Start Time (HH:MM)"
            fullWidth
            placeholder="02:00"
            value={scheduleData.starttime}
            onChange={(e) => setScheduleData({ ...scheduleData, starttime: e.target.value })}
            helperText="24-hour format (e.g., 02:00 for 2 AM)"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBackupScheduleDialog(false);
              setEditingJob(null);
              setScheduleData({ dow: '', starttime: '' });
            }} 
            disabled={actionLoading[`${vm.vmid}-schedule`]}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleScheduleBackup} 
            variant="contained" 
            disabled={!scheduleData.dow || !scheduleData.starttime || actionLoading[`${vm.vmid}-schedule`]}
          >
            {actionLoading[`${vm.vmid}-schedule`] ? 'Saving...' : (editingJob ? 'Update' : 'Schedule')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Operation Status */}
      {operationInProgress && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
          <LinearProgress />
          <Alert 
            severity="info" 
            sx={{ borderRadius: 0 }}
            icon={
              <SyncIcon 
                sx={{ 
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} 
              />
            }
          >
            <strong>{operationInProgress}</strong> Please wait...
          </Alert>
        </Box>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
      
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        onConfirm={() => {
          confirmDialog.action();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default VMDetails;
